import * as EM from 'ApplicationEntry/components/WorkFlow/ErrorMessages';
import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import {isInvalidDate, isValidDate} from 'ApplicationEntry/models/Date';
import {isValidPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {GroupBillingAgent} from 'ApplicationEntry/models/GroupBillingAgent';
import { ApplicantDemographics } from 'ApplicationEntry/models/ApplicantDemographics';
import {PrimaryBeneficiary, relations} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {
  isEmptySocialNumber,
  isValidSocialSecurityNumber,
  SocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import States from 'ApplicationEntry/models/States';
import {
  isEmptyZipCode,
  isValidZipCode,
  ZipCode,
  unmaskZipCode,
  ValidZipCode,
  maskZipCode,
} from 'ApplicationEntry/models/ZipCode';
import React, {useEffect, useState} from 'react';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';
import './PrimaryBeneficiary.sass';

import {
  Button,
  createMuiTheme,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
  Divider,
} from '@material-ui/core';
import {red} from '@material-ui/core/colors';
import {KeyboardDatePicker} from '@material-ui/pickers';
import * as ErrorMessages from '../ErrorMessages';
import * as MA from 'ApplicationEntry/models/ModalApplication';
import * as CD from 'ApplicationEntry/ducks/caseDetails';
import { useDispatch } from 'react-redux';
import { getManagedStatus } from 'ApplicationEntry/api/awdApi';
import { Alert } from '@material-ui/lab';

const theme = createMuiTheme({
  palette: {
    secondary: red,
  },
});

type PrimaryBeneficiaryProps = {
  index: number;
  key: number;
  primaryBeneficiaryIndex: number;
  editPrimaryBeneficiary: boolean;
  removePrimaryBeneficiary: () => void;
  changeFirstName: (value: string) => void;
  changeMiddleName: (value: string) => void;
  changeLastName: (value: string) => void;
  changeRelationship: (value: string) => void;
  changeDOB: (value: Date | '') => void;
  changeSSN: (value: string) => void;
  changePercent: (value: number) => void;
  changeAddress: (value: string) => void;
  changeAddressLine2: (value: string) => void;
  changeCity: (value: string) => void;
  changeState: (value: string) => void;
  changeZip: (value: ZipCode) => void;
  changePhone: (value: PhoneNumber) => void;
  changeSignatureDate: (value: Date | '') => void;
  members: PrimaryBeneficiary;
  primaryBeneficiaryListLength: number;
  totalPercent: number;
  modalApplication: MA.InvalidModalApplication | MA.ValidModalApplication;
  groupBillingAgent: GroupBillingAgent;
  applicantDemographics: ApplicantDemographics;
};

const getSSNErrorMessage = (ssn: SocialSecurityNumber) =>
  !isValidSocialSecurityNumber(ssn) ? EM.INVALID_SSN_FORMAT : null;
const getPhoneErrorMessage = (phone: PhoneNumber) => (!isValidPhoneNumber(phone) ? EM.INVALID_PHONE_FORMAT : null);
const getZipErrorMessage = (zip: ZipCode) => (!isValidZipCode(zip) ? EM.INVALID_ZIP_FORMAT : null);

const PrimaryBeneficiaryData = (props: PrimaryBeneficiaryProps) => {
  const {
    members,
    changeAddress,
    changeAddressLine2,
    changeCity,
    changeDOB,
    changeFirstName,
    changeLastName,
    changeMiddleName,
    changePercent,
    changeRelationship,
    changeSSN,
    changeState,
    changeZip,
    changePhone,
    changeSignatureDate,
    totalPercent,
    modalApplication,
    groupBillingAgent,
    applicantDemographics,
  } = props;

  const isInvalidDate = (date: Date) => isNaN(date.getTime());
  const {application} = modalApplication;
  const getPercentErrorMessage = (percent: number) => (totalPercent > 100 ? EM.INVALID_PERCENTAGE : null);

  const [stateLabel, setStateLabel] = useState<HTMLLabelElement | null>();
  const [relationshipLabel, setRelationshipLabel] = useState<HTMLLabelElement | null>();
  const [ZipBlurred, setBlurZip] = useState(false);

  const showPercentError = totalPercent > 100;
  const showDobError = members.dob !== '' && !isValidDate(members.dob);  
  const [signatureDateblur, setSignatureDateBlur] = useState(false);
  const showSSNError =
    !isValidSocialSecurityNumber(members.socialSecurityNumber) && !isEmptySocialNumber(members.socialSecurityNumber);
  const showPhoneError = members.phone.phoneNumber !== '' && !isValidPhoneNumber(members.phone);
  const showZipError = ZipBlurred && !isEmptyZipCode(members.zip) && !isValidZipCode(members.zip);  
  const showSignatureDateError = signatureDateblur && members.signatureDate !== '' && isInvalidDate(members.signatureDate);

  const handlePrimaryBeneficiary = () => {
    props.removePrimaryBeneficiary();
  };

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const dispatch = useDispatch();  
  const fetchUserData = async () => {
    if (application.policyRecord.status.value === 'BEEAPPRV') {
      let ssn1 = application.policyRecord.caseNumber.value;
      const user = await getManagedStatus(ssn1);

      let pacDataResultData1;
      if (user['right']) {
        pacDataResultData1 = user['right']['beneficiaryCaseDetail'];
        const { managedStatus } = pacDataResultData1;
        let emp;
        if (managedStatus === null) {
          emp = "AHL Managed";
        } else {
          emp = managedStatus.valueOf().toString();
        }
        if (emp === "Employer Managed") {
          dispatch(CD.updateManagedStatusFlag(true));
        } else {
          dispatch(CD.updateManagedStatusFlag(false));
        }
        dispatch(CD.updateManagedStatus(managedStatus));
      } else if(user['left']){
        dispatch(CD.updateManagedStatusFlag(false));
      }
    }
  };


  useEffect(() => {
    fetchUserData();
  }, []);
  
  return (
    <>
      <div className="primary-beneficiary-section__primary-beneficiary-view">
        <div className="primary-beneficiary-section__primary-beneficiary-view__sub-title-container">
          <Typography
            variant="subtitle1"
            className="primary-beneficiary-section__primary-beneficiary-view__sub-title-container-title"
          >
            Primary Beneficiary{' '}
            {props.editPrimaryBeneficiary
              ? props.primaryBeneficiaryIndex + 1
              : props.primaryBeneficiaryListLength + props.index + 1}
          </Typography>
          {props.index !== 0 ? (
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePrimaryBeneficiary}
                className="primary-beneficiary-section__primary-beneficiary-view-delete-btn"
              >
                REMOVE PRIMARY BENEFICIARY
              </Button>
            </ThemeProvider>
          ) : null}
        </div>
        <form
          noValidate
          autoComplete="off"
          className="primary-beneficiary-section__primary-beneficiary__owner-info-section"
        >          
        {application.caseDetails.managedStatusFlag.valueOf().toString() === 'true' ? (
          <Alert severity="info" className="case-details-section__reserved-alert">
            Beneficiary is Employer Managed.
          </Alert>
          ) : null}
          <div className="primary-beneficiary-section__primary-beneficiary__owner-info-section__row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <TextField
                  id="first-name"
                  label="First Name"
                  variant="outlined"
                  placeholder="Enter First Name"
                  margin="normal"
                  className="owner-info-section__text-field"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}                  
                  onChange={e => changeFirstName(e.target.value)}
                  value={members.firstName.value}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="middle-name"
                  label="Middle Name"
                  variant="outlined"
                  placeholder="Enter Middle Name"
                  margin="normal"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  value={members.middleName.value}
                  onChange={e => changeMiddleName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="last-name"
                  label="Last Name"
                  variant="outlined"
                  placeholder="Enter Last Name"
                  margin="normal"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  value={members.lastName.value}
                  onChange={e => changeLastName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="percent"
                  label="Percent"
                  variant="outlined"
                  placeholder="Enter Percent"
                  margin="normal"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  error={showPercentError}
                  helperText={showPercentError ? getPercentErrorMessage(members.percent) : ''}
                  type="number"
                  value={members.percent}
                  onChange={e =>
                    isNaN(parseInt(e.target.value)) ? changePercent(0) : changePercent(parseInt(e.target.value))
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  id="ssn"
                  label="SSN"
                  variant="outlined"
                  placeholder="XXX-XX-XXXX"
                  inputProps={{
                    maxLength: 11,
                  }}
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  error={showSSNError}
                  helperText={showSSNError ? getSSNErrorMessage(members.socialSecurityNumber) : ''}
                  value={maskTaxId(members.socialSecurityNumber.value)}
                  onChange={e => changeSSN(unMaskTaxId(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="owner-info-section__text-field">
                  <InputLabel ref={setRelationshipLabel} id="dependent-label">
                    Relationship
                  </InputLabel>
                  <Select
                    labelId="label-dependent"
                    id="dependent"
                    labelWidth={relationshipLabel?.clientWidth}
                    disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                    value={members.relationship.value}
                    onChange={e => changeRelationship(e.target.value as ApplicantType)}
                    displayEmpty
                  >
                    <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                    {relations.map(rel => (
                      <MenuItem value={rel} key={rel}>
                        {rel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <KeyboardDatePicker
                  inputVariant="outlined"
                  label="Date of Birth"
                  format="MM/dd/yyyy"
                  variant="inline"
                  className="owner-info-section__text-field"
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  value={members.dob || null}
                  error={showDobError}
                  helperText={showDobError ? EM.REQUIRED_MESSAGE : ''}
                  onChange={date => changeDOB(date?.toJSDate() || '')}
                  maxDate={yesterday}
                  autoOk={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="phone-number"
                  label="Phone Number"
                  variant="outlined"
                  placeholder="(XXX)-XXX XXXX"
                  inputProps={{
                    maxLength: 14,
                  }}
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  value={maskPhoneNumber(members.phone.phoneNumber)}
                  error={showPhoneError}
                  helperText={showPhoneError ? getPhoneErrorMessage(members.phone) : ''}
                  onChange={e => changePhone(ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value)))}
                  className="owner-info-section__text-field"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  id="address"
                  label="Address"
                  variant="outlined"
                  placeholder="Enter Address"
                  margin="normal"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  value={members.address.value}
                  onChange={e => changeAddress(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="addressline2"
                  label="Address Line 2"
                  variant="outlined"
                  placeholder="Enter Address Line 2"
                  margin="normal"
                  fullWidth
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="owner-info-section__text-field"
                  value={members.addressLine2.value}
                  onChange={e => changeAddressLine2(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="city"
                  label="City"
                  variant="outlined"
                  placeholder="Enter City"
                  margin="normal"
                  className="owner-info-section__text-field"
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  value={members.city.value}
                  onChange={e => changeCity(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="owner-info-section__text-field">
                  <InputLabel ref={setStateLabel} id="owner-state">
                    State
                  </InputLabel>
                  <Select
                    labelId="label-owner-state"
                    id="select-owner-state"
                    labelWidth={stateLabel?.clientWidth}
                    disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                    value={members.state.value}
                    onChange={e => changeState(e.target.value as ApplicantType)}
                    displayEmpty
                  >
                    <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                    {States.map(stateCode => (
                      <MenuItem value={stateCode} key={stateCode}>
                        {stateCode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  id="owner-zip"
                  label="Zip"
                  variant="outlined"
                  placeholder="XXXXX-XXXX"
                  inputProps={{
                    maxLength: 10,
                  }}
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  className="primary-beneficiary-section__zip"
                  error={showZipError}
                  helperText={showZipError ? getZipErrorMessage(members.zip) : ''}
                  onBlur={() => setBlurZip(true)}
                  value={maskZipCode(members.zip.value)}
                  onChange={e => changeZip(ValidZipCode.create(unmaskZipCode(e.target.value)))}
                />
              </Grid>
              {/* <Grid item xs={12} md={3}>
                <KeyboardDatePicker
                  inputVariant="outlined"
                  label="Signature Date"
                  format="MM/dd/yyyy"
                  variant="inline"
                  className="owner-info-section__text-field"
                  disabled={(application.caseDetails.managedStatusFlag.valueOf().toString() === 'true')}
                  value={(members.signatureDate.valueOf().toString() === "NaN" ? null : members.signatureDate) || null}
                  onBlur={() => setSignatureDateBlur(true)}
                  error={showSignatureDateError}
                  helperText={showSignatureDateError ? ErrorMessages.INVALID_DATE : ''}
                  onChange={date => changeSignatureDate(date?.toJSDate() || '')}
                  maxDate={yesterday}
                  autoOk={true}
                />
              </Grid> */}
            </Grid>
          </div>
{/*           
          {application.caseDetails.managedStatusFlag.valueOf().toString() !== 'true' ? (
            <Alert severity="success" className="case-details-section__reserved-alert">
              Case is Employer Managed.
            </Alert>
            ) : null} */}
        </form>
        <Divider className="application-form__actions-divider" />
      </div>
    </>
  );
};

export default PrimaryBeneficiaryData;
